import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../../core/services/auth.service';

@Injectable({ providedIn: 'root' })
export class UserLocalStorageService {
  private authService = inject(AuthService);

  private static USER_PREFIX = 'US';

  private readonly $user = toSignal(this.authService.user$);
  private readonly $userId = computed(() => this.$user()?.userId);

  /**
   * store user specific value in local storage
   */
  public static setUserValue(key: string, value: string, userId: string): void {
    if (!userId) {
      throw new Error('No user id available');
    }
    const storageKey = UserLocalStorageService.USER_PREFIX + ':' + userId + ':' + key;
    UserLocalStorageService.setValue(storageKey, value);
  }

  public static getUserValue(key: string, userId: string): string | null {
    if (!userId) {
      throw new Error('No user id available');
    }
    return UserLocalStorageService.getValue(UserLocalStorageService.USER_PREFIX + ':' + userId + ':' + key);
  }

  public static removeUserValue(key: string, userId: string): void {
    if (!userId) {
      throw new Error('No user id available');
    }
    const storageKey = UserLocalStorageService.USER_PREFIX + ':' + userId + ':' + key;
    UserLocalStorageService.removeValue(storageKey);
  }

  /**
   * set persistent value
   */
  static setValue(key: string, value: string): void {
    if (!value) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  }

  /**
   * remove persistent value
   */
  static removeValue(key: string) {
    localStorage.removeItem(key);
  }

  /**
   * get persistent value
   */
  static getValue(key: string): string | null {
    return localStorage.getItem(key);
  }

  static getValueDefault<T>(key: string, defaultValue: T): T {
    let value = JSON.parse(localStorage.getItem(key) ?? '');

    if (!value) {
      value = defaultValue;
    }
    return value;
  }

  /**
   * get user specific persistent value from local storage
   */
  public getUserValue(key: string): string {
    const userId = this.$userId();

    if (!userId) {
      throw new Error('No user id available');
    }
    return UserLocalStorageService.getValue(UserLocalStorageService.USER_PREFIX + ':' + userId + ':' + key) ?? '';
  }

  /**
   * store user specific value in local storage
   */
  public setUserValue(key: string, value: string): void {
    const userId = this.$userId();

    if (!userId) {
      throw new Error('No user id available');
    }
    UserLocalStorageService.setUserValue(key, value, userId);
  }

  public removeUserValue(key: string): void {
    const userId = this.$userId();

    if (!userId) {
      throw new Error('No user id available');
    }
    UserLocalStorageService.removeUserValue(key, userId);
  }

}
